import PropTypes from 'prop-types';
import { Component } from 'react';
import {
  Button,
  ControlLabel,
  DropdownButton,
  FormControl,
  FormGroup,
  InputGroup,
  MenuItem,
} from 'react-bootstrap';

import Locator from '../../../shared/Locator';
import { window } from '../../globals';
import { post } from '../../redux/api';
import utils from '../../utils';
import Icon, { icons } from '../generic/Icon';
import { DocsLink } from '../generic/Link';
import Loader from '../generic/Loader';
import type { Build } from '../../types';

type ICreateProjectPageProps = {
  onSubmit?: Function;
  onHide?: Function;
  show?: boolean;
};

type ICreateProjectPageState = {
  fetch_mode?: string;
  repo_url?: string;
  auth?: string;
  reference?: string;
  build?: Build;
  loading?: boolean;
  error?: string;
  private?: boolean;
};

class CreateProjectPage extends Component<ICreateProjectPageProps, ICreateProjectPageState> {
  static propTypes = {
    onSubmit: PropTypes.func,
    onHide: PropTypes.func,
  };

  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  unsubscribeBuild;

  constructor(props) {
    super(props);
    this.state = {
      fetch_mode: null,
      repo_url: '',
      auth: '',
      reference: '',
      build: null,
      loading: false,
      error: null,
      private: true,
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!this.props.show && nextProps.show) {
      this.setState({
        fetch_mode: null,
        repo_url: '',
        auth: '',
        reference: '',
        build: null,
        loading: false,
        error: null,
        private: nextProps.isPrivate,
      });
    }
  }

  onModeSelect = mode => {
    this.setState({ fetch_mode: mode });
  };

  onRepoFetch = () => {
    this.setState({ loading: true, error: null });
    post(
      '/api/projects/build',
      {
        auth: this.state.auth
          ? {
              type: 'basic',
              key: this.state.auth,
            }
          : null,
      },
      {
        query: {
          url: utils.nuc(this.state.repo_url),
          mode: this.state.fetch_mode,
          public: !this.state.private,
          reference: this.state.reference,
        },
      },
    ).then(resp => {
      const newBuild = resp.data;
      if (!newBuild) {
        throw new Error('Unable to create build.');
      }

      const loc = new Locator(newBuild.locator);
      this.context.router.push(`/projects/${utils.nuc(loc.toPackageString())}`);
      return newBuild;
    }).catch(err => {
      this.setState({ loading: false, error: err.data });
    });
  };

  onInputURL = ev => {
    this.setState({ repo_url: ev.target.value });
    const new_mode = this.parseURLMode(ev.target.value);
    if (new_mode) {
      this.onModeSelect(new_mode);
    }
  };

  static modes = {
    git(url) {
      return url.match(/(^git:\/\/.+|.+\.git$)/gi) || url.match(/^(https?:\/\/)?github.com/gi);
    },
    go(_url) {},
    npm(url) {
      return url.match(/^(https?:\/\/)?(www\.)?npmjs\.(org|com)\/(package\/)?[^\/]+$/gi);
    },
    archive(url) {
      return url.match(/(.+\.(tar|zip)(\.gz)?$)/gi);
    },
    mvn(url) {
      return url.match(/(maven\.|mvnrepository)/gi);
    },
    nuget(url) {
      return url.match(/^(https?:\/\/)?(www\.)?nuget\.(org|com)\/packages\//gi);
    },
    gem(url) {
      return url.match(/^(https?:\/\/)?(www\.)?rubygems\.(org|com)\/gems\//gi);
    },
    pip(_url) {},
    cargo(url) {
      return url.match(/^(https?:\/\/)(www\.)?crates\.io(\/crates)?/gi);
    },
    pod(url) {
      return url.match(/^(https?:\/\/)?(www\.)?cocoapods\.(org)\/pods\//gi);
    },
  };

  parseURLMode = url => {
    const keys = Object.keys(CreateProjectPage.modes);
    for (let i = 0; i < keys.length; i++) {
      if (CreateProjectPage.modes[keys[i]](url.replace(/^\s+|\s+$/gm, ''))) {
        return keys[i];
      }
    }
  };

  render() {
    const import_mode_selector = (
      <DropdownButton
        componentClass={InputGroup.Button}
        id="modalCreateProjectDropdownButton"
        title={
          (this.state.fetch_mode ? (
            <span
              className="badge"
              style={{
                backgroundColor: utils.hashColor(this.state.fetch_mode, 0.6),
              }}>
              {this.state.fetch_mode}
            </span>
          ) : (
            'Fetchers...'
          )) as {}
        }
        className="mode"
        bsSize="large"
        onSelect={this.onModeSelect}
        disabled={this.state.loading}
        pullRight>
        <MenuItem header>Version Control Systems</MenuItem>
        <MenuItem eventKey="git">Git</MenuItem>
        {/* <MenuItem eventKey='hg'>Mercurial (hg)</MenuItem>
        <MenuItem eventKey='svn'>SVN</MenuItem> */}
        <MenuItem divider />
        <MenuItem header>Package Hosts & Fetchers</MenuItem>
        <MenuItem eventKey="npm">NPM (npmjs.com)</MenuItem>
        <MenuItem eventKey="mvn">Apache Maven</MenuItem>
        <MenuItem eventKey="pod">CocoaPods</MenuItem>
        <MenuItem eventKey="go">Golang (beta)</MenuItem>
        <MenuItem eventKey="nuget">Nuget (beta)</MenuItem>
        <MenuItem eventKey="pip">Pip (beta)</MenuItem>
        <MenuItem eventKey="gem">Gem (beta)</MenuItem>
        <MenuItem eventKey="cargo">Cargo (beta)</MenuItem>
        {/* <MenuItem divider/>
        <MenuItem header>Other</MenuItem>
        <MenuItem eventKey='archive'>Archive</MenuItem> */}
      </DropdownButton>
    );
    const build_enabled = this.state.repo_url && this.state.fetch_mode;

    let config_panel;
    switch (this.state.fetch_mode) {
      case 'git':
        config_panel = (
          <div className="row">
            <FormGroup controlId="branchAndOAuthForm">
              <div className="col-md-6">
                <ControlLabel>Branch/Tag/Commit Id</ControlLabel>
                <FormControl
                  type="text"
                  placeholder="(optional)"
                  value={this.state.reference}
                  onChange={(ev: {}) => {
                    this.setState({ reference: ev.target.value });
                  }}
                  disabled={this.state.loading}
              />
              </div>
              <div className="col-md-6">
                <ControlLabel>
                  <span>
                    Authentication (user:pass) or (token)
                  </span>
                </ControlLabel>
                <FormControl
                  placeholder="(optional)"
                  type="text"
                  value={this.state.auth}
                  onChange={(ev: {}) => {
                    this.setState({ auth: ev.target.value });
                  }}
                  disabled={this.state.loading}
              />
              </div>
            </FormGroup>
          </div>
        );
        break;
      default:
        break;
    }

    return (
      <div className="container margin-top">
        {!window.on_prem && <div className="alert alert-warning">Warning: importing public projects via URL is risky and should only be done if you know what you are doing.</div>}
        <div className="card">
          <div className="repo-input">
            <label className="control-label">
              Repository or Package Source URL:{' '}
              <DocsLink
                to="supported-languages"
                className="btn-show-import-docs"
              >
                (Supported Environments)
              </DocsLink>
            </label>
            <FormGroup controlId="repoInputForm">
              <InputGroup className="input-group input-group-lg">
                <FormControl
                  type="text"
                  bsSize="large"
                  className="input-repo-url input-block-level"
                  placeholder="https://github.com/benjamn/recast"
                  value={this.state.repo_url}
                  onChange={this.onInputURL}
                  disabled={this.state.loading}
                />
                {import_mode_selector}
              </InputGroup>
            </FormGroup>
          </div>
          {config_panel}
          <br />
          {this.state.error ? <div className="alert alert-error">{this.state.error}</div> : null}
          {this.state.loading && !this.state.build ? (
            <div className="text-center">
              <Loader />
            </div>
          ) : (
            ''
          )}
          <div className="text-center">
            <Button
              bsSize="large"
              className="btn-raised"
              onClick={this.onRepoFetch}
              bsStyle="primary"
              disabled={this.state.loading || !build_enabled}>
              <Icon icon={icons.download} /> Fetch & Import
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export default CreateProjectPage;
